import { useState } from "react"
import type { PaginationState } from "@tanstack/react-table"

import { TableVisualization } from "~/ui-rtk/components/ui/charts"
import { Loader } from "~/ui-rtk/components/ui/common"
import type {
  TableVisualizationColumn,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import { formatNumber } from "~/ui-rtk/utils/format"

import { CHART_ID, type SubsequentGrain } from "../constants"
import { DEFAULT_SORTING, type SubsequentRow } from "../utils/subsequent-rows"

export type SubsequentTableProps = {
  grain: SubsequentGrain
  /** "First orders" when only sequence 1 is selected, else "Original orders". */
  originalLabel: string
  rows: SubsequentRow[]
  isLoading: boolean
  hasSelection: boolean
}

const percent = (value: number | null) =>
  value === null ? "--" : `${(value * 100).toFixed(1)}%`

const integer = (value: number) => formatNumber(value, { decimals: 0 })

const currency = (value: number) =>
  formatNumber(value, { currency: true, decimals: 0 })

const days = (value: number | null) =>
  value === null ? "--" : formatNumber(value, { decimals: 0 })

function ItemCell({
  row,
  grain,
}: {
  row: SubsequentRow
  grain: SubsequentGrain
}) {
  return (
    <div className="max-w-xs">
      <div className="font-medium text-white truncate">{row.title}</div>
      {grain === "product" && row.handle && (
        <div className="text-xs text-white/50 truncate">{row.handle}</div>
      )}
    </div>
  )
}

export const buildColumns = (
  grain: SubsequentGrain,
  originalLabel: string,
): TableVisualizationColumn[] => [
  {
    key: "title",
    header:
      grain === "product" ? "Next-order product" : "Next-order SKU prefix",
    type: "text",
    width: 320,
    blurInDemo: grain === "product",
    render: (row: TMetric) => (
      <ItemCell row={row as unknown as SubsequentRow} grain={grain} />
    ),
  },
  {
    // The business question: of everyone whose original order held the
    // selection, how many came back for THIS item? Customers who have not
    // reordered count against it, so it is the conservative headline number.
    key: "allOrdersShare",
    header: `${originalLabel} converting to this item`,
    type: "number",
    width: 230,
    render: (row: TMetric) => (
      <div className="text-right font-semibold text-green-bright">
        {percent((row as unknown as SubsequentRow).allOrdersShare)}
      </div>
    ),
  },
  {
    key: "subsequentOrders",
    header: "Next orders",
    type: "number",
    width: 130,
    render: (row: TMetric) => (
      <div className="text-right font-medium">
        {integer((row as unknown as SubsequentRow).subsequentOrders)}
      </div>
    ),
  },
  {
    // Same numerator divided only by the orders that were followed by another
    // order: of the customers who did come back, how many picked this item.
    key: "nextOrderShare",
    header: "Share of repeaters’ next orders",
    type: "number",
    width: 210,
    render: (row: TMetric) => (
      <div className="text-right">
        {percent((row as unknown as SubsequentRow).nextOrderShare)}
      </div>
    ),
  },
  {
    key: "subsequentQuantity",
    header: "Units",
    type: "number",
    width: 110,
    render: (row: TMetric) => (
      <div className="text-right">
        {integer((row as unknown as SubsequentRow).subsequentQuantity)}
      </div>
    ),
  },
  {
    key: "subsequentRevenue",
    header: "Next-order revenue",
    type: "number",
    width: 160,
    render: (row: TMetric) => (
      <div className="text-right">
        {currency((row as unknown as SubsequentRow).subsequentRevenue)}
      </div>
    ),
  },
  {
    key: "avgDaysToNext",
    header: "Avg days to next order",
    type: "number",
    width: 170,
    render: (row: TMetric) => (
      <div className="text-right">
        {days((row as unknown as SubsequentRow).avgDaysToNext)}
      </div>
    ),
  },
]

export default function SubsequentTable({
  grain,
  originalLabel,
  rows,
  isLoading,
  hasSelection,
}: SubsequentTableProps) {
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })

  if (!hasSelection) {
    return (
      <div className="bg-system-blue-800 rounded-lg p-6 text-gray-light-2">
        Choose an analysis type and pick a{" "}
        {grain === "product" ? "product" : "SKU prefix"} above to see what
        customers bought in their next order.
      </div>
    )
  }

  if (!isLoading && rows.length === 0) {
    return (
      <div className="bg-system-blue-800 rounded-lg p-6 text-gray-light-2">
        No next-order purchases found for this selection in the chosen period.
      </div>
    )
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-basic-black/50 rounded-lg">
          <Loader size={50} />
        </div>
      )}
      <TableVisualization
        metrics={rows as unknown as TMetric[]}
        columnConfig={buildColumns(grain, originalLabel)}
        chartId={CHART_ID}
        paginationState={paginationState}
        setPaginationState={setPaginationState}
        enablePagination={true}
        manualPagination={false}
        sortBy={DEFAULT_SORTING[0].id}
        sortDesc={DEFAULT_SORTING[0].desc}
      />
    </div>
  )
}
